/**
 * The files behind a quality issue count.
 *
 * The analysis screen has always been able to say "3 dosyada: Netlik
 * yetersiz." and never able to say *which* three. That is the gap this closes:
 * a count is a claim about specific files, and a claim you cannot inspect is
 * one the reader has to take on faith.
 *
 * Four rules it is built around, each of which is a way this screen could
 * quietly lie:
 *
 * 1. **Only measured issues.** A row shows exactly the issue records the
 *    backend stored for that file, with the measured value and the threshold
 *    where both exist. Nothing is inferred, and a file whose verdict is poor
 *    with no issue records says so rather than being given a plausible reason.
 * 2. **Identity comes from the id, never the filename.** The filename is a
 *    label the user typed; the media type, format and dimensions come from the
 *    asset record. Two files called `IMG_0042.jpg` are two different files and
 *    this screen must never treat them as one.
 * 3. **Filters are built from what is here.** The chips are the codes actually
 *    present in this session's assessments - not a catalogue of every code the
 *    backend can emit, which would offer filters that select nothing.
 * 4. **A delete refreshes server truth.** It does not splice the row out of a
 *    local array; it asks the session to reload, so what is on screen after
 *    the delete is what the server has.
 *
 * Actions are only the ones that exist. Preview and delete are real endpoints.
 * "Re-capture" is offered once, for the whole set, as the correction-session
 * flow that already exists - and deliberately *not* per file, because no flow
 * re-takes one specific photograph and a button implying otherwise would be a
 * promise the product cannot keep.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiUrl } from "@/api/config";
import type {
  MediaAsset,
  MediaEnhancement,
  ModelCapabilities,
  QualityAssessment,
  QualityIssueSeverity,
} from "@/api/types";
import { enhanceMedia, getModelCapabilities, upscaleMedia } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button, StatusBadge } from "@/components/ui";
import { MEDIA_TYPE_LABELS, SEVERITY_LABELS, SEVERITY_TONES } from "@/i18n/terms";
import { formatDateTime, formatDecimal, formatDimensions } from "@/lib/format";
import { paths } from "@/app/paths";
import { DeleteMediaButton } from "./DeleteMediaButton";
// From `mediaAnalysis`, not from the analysis centre that renders this
// dialog: importing back up would make the two modules a cycle.
import { qualityIssueText } from "./mediaAnalysis";
import { eligibleFor, restorationActions } from "./restorationActions";
import { runBulk } from "./bulkMedia";
import { useDialogFocus } from "@/lib/useDialogFocus";

/** One file that failed assessment, paired with the assessment that failed it. */
export interface ProblemMedia {
  asset: MediaAsset;
  assessment: QualityAssessment;
}

interface Props {
  rows: readonly ProblemMedia[];
  /** Which session these came from, so a row says where it lives. */
  sessionLabel: string;
  projectId: string;
  /** The code the reader clicked, pre-selected. `null` shows everything. */
  initialCode: string | null;
  onClose: () => void;
  /** Re-read the session's media from the server. */
  onChanged: () => void;
}

/** The measured numbers behind one issue, when the backend recorded both. */
function Measurement({ measured, threshold }: { measured: number | null; threshold: number | null }) {
  if (measured === null || threshold === null) return null;
  // The product's own decimal formatting, not `toFixed`: these render beside
  // scores everywhere else in the interface and a dot here would be the only
  // number on the screen using a different separator. Two places, because the
  // digits past the second are noise the reader cannot act on.
  return (
    <span className="problem-media__measure">
      ölçülen {formatDecimal(measured)} · eşik {formatDecimal(threshold)}
    </span>
  );
}

/**
 * The severity word, or an honest stand-in.
 *
 * `SEVERITY_LABELS` is exhaustive over the severities this build knows. A
 * value from a newer backend would index to `undefined` and render an empty
 * badge - a coloured rectangle with no word in it, which is both meaningless
 * and status-by-colour-alone. This says that a severity exists instead.
 */
function severityLabel(severity: QualityIssueSeverity): string {
  return SEVERITY_LABELS[severity] ?? "Ölçülen bulgu";
}

function ProblemRow({
  row,
  sessionLabel,
  capabilities,
  onChanged,
}: {
  row: ProblemMedia;
  sessionLabel: string;
  /**
   * What this installation can actually run, or null while it is being read.
   * Null keeps every learned action disabled: a control that lights up before
   * the answer arrives is a control that can be pressed into a 404.
   */
  capabilities: ModelCapabilities | null;
  onChanged: () => void;
}) {
  const { asset, assessment } = row;
  const issues = assessment.issues ?? [];
  const [previewFailed, setPreviewFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [enhancement, setEnhancement] = useState<MediaEnhancement | null>(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhancementMessage, setEnhancementMessage] = useState<string | null>(null);
  //: Why a control that was pressed did nothing. Shown rather than swallowed:
  //: a disabled button whose reason only exists in a tooltip is unreadable on
  //: a touchscreen, which is where most of these photographs come from.
  const [restorationNote, setRestorationNote] = useState<string | null>(null);
  /** The factor currently running, so the other button greys with it. */
  const [upscaling, setUpscaling] = useState<number | null>(null);
  const dimensions = formatDimensions(asset.width, asset.height);
  const actions = restorationActions(
    asset,
    { state: "assessed", assessment },
    capabilities,
  );

  return (
    <li className="problem-media__row" aria-label={asset.original_filename}>
      <div className="problem-media__thumb">
        {previewFailed ? (
          <span className="problem-media__thumb-fallback">
            {/* No placeholder picture standing in for the file: a preview that
                could not be produced is a fact, not a missing image. */}
            Önizleme oluşturulamadı
          </span>
        ) : (
          <img
            src={apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`)}
            alt={`${asset.original_filename} önizlemesi`}
            loading="lazy"
            decoding="async"
            onError={() => setPreviewFailed(true)}
          />
        )}
      </div>

      <div className="problem-media__body">
        {/* User text, rendered as text. Nothing here builds markup from it,
            and nothing reads meaning out of it - the type below comes from the
            asset record, not from the extension in this string. */}
        <p className="problem-media__name" title={asset.original_filename}>
          {asset.original_filename}
        </p>
        <p className="problem-media__meta">
          {MEDIA_TYPE_LABELS[asset.media_type]}
          {dimensions !== "—" && <> · {dimensions}</>}
          {" · "}
          {sessionLabel}
          {" · "}
          {formatDateTime(asset.created_at)}
        </p>

        {issues.length > 0 ? (
          <ul className="problem-media__issues">
            {issues.map((issue) => (
              <li key={`${issue.code}-${issue.severity}`}>
                {/* The design-system badge, not a bare chip: `chip--danger`
                    was a class nobody had styled, so a critical finding and a
                    warning looked identical. `StatusBadge` carries a tone
                    *and* a dot, and the severity word is always present - so
                    the distinction survives greyscale. */}
                <StatusBadge tone={SEVERITY_TONES[issue.severity] ?? "neutral"}>
                  {severityLabel(issue.severity)}
                </StatusBadge>
                <span>{qualityIssueText(issue.code)}</span>
                <Measurement measured={issue.measured_value} threshold={issue.threshold} />
              </li>
            ))}
          </ul>
        ) : (
          // The verdict is stored per file and the issue records are separate
          // rows; a file can be rejected with none of them. Inventing a reason
          // here would be the exact failure this screen exists to prevent.
          <p className="problem-media__issues problem-media__issues--none">
            Bu dosya yetersiz bulundu; ayrıntılı bir ölçüm kaydı yok.
          </p>
        )}
      </div>

      <div className="problem-media__actions">
        {/*
          Opens the picture here, not in a browser tab containing a bare JPEG.
          The tab had no filename, no verdict and no way back, so deciding
          which of forty files to re-take meant opening and closing forty tabs.
        */}
        <Button
          variant="secondary"
          onClick={() => setExpanded((open) => !open)}
          ariaLabel={`${asset.original_filename} önizlemesini ${expanded ? "küçült" : "büyüt"}`}
        >
          {expanded ? "Küçült" : "Büyüt"}
        </Button>
        <DeleteMediaButton asset={asset} onDeleted={onChanged} />
        {/*
          Offered only where the backend can actually perform it.
          `canEnhance` mirrors `application/enhancement.py`: a measured,
          recoverable issue. Without that gate the button ran on any
          photograph, failed, and reported "bu dosyada güvenle düzeltilebilen
          ölçülmüş bir sorun yok" - the product offering work it could not do.
        */}
        {actions.map((action) => {
          if (!action.visible) return null;
          // A learned action with no model anywhere is not rendered at all -
          // see `restorationActions` for why a permanent grey button is a
          // promise. Everything else is rendered, and disabled controls carry
          // their reason as a tooltip *and* as the sentence below.
          if (action.kind === "super_resolution" && action.enabled) {
            /*
              The one learned action that really runs. Drawn as the warning
              followed by one button per factor, rather than as a button that
              opens a dialog carrying the warning: a sentence somebody has to
              click through is a sentence somebody clicks through. Here it is
              above the controls and cannot be reached past.
            */
            return (
              <div key={action.kind} className="stack-tight">
                {action.caveat && (
                  <span className="faint" id={`${action.kind}-caveat`}>
                    {action.caveat}
                  </span>
                )}
                <div className="row">
                  {action.scales.map((scale) => (
                    <Button
                      key={scale}
                      variant="secondary"
                      busy={upscaling === scale}
                      disabled={upscaling !== null}
                      ariaDescribedBy={
                        action.caveat ? `${action.kind}-caveat` : undefined
                      }
                      onClick={async () => {
                        setUpscaling(scale);
                        setEnhancementMessage(null);
                        setRestorationNote(null);
                        try {
                          setEnhancement(await upscaleMedia(asset.id, scale));
                        } catch (error) {
                          setEnhancementMessage(
                            error instanceof ApiError && error.status === 503
                              ? "Çözünürlük artırma şu anda kullanılamıyor; orijinal dosya değişmedi."
                              : "Çözünürlük artırılamadı; orijinal dosya değişmedi.",
                          );
                        } finally {
                          setUpscaling(null);
                        }
                      }}
                    >
                      {`${action.label} ${scale}x`}
                    </Button>
                  ))}
                </div>
              </div>
            );
          }
          if (action.kind !== "enhance") {
            return (
              <Button
                key={action.kind}
                variant="secondary"
                disabled={!action.enabled}
                title={action.reason ?? undefined}
                ariaLabel={`${action.label}${action.reason ? ` — ${action.reason}` : ""}`}
                onClick={() => setRestorationNote(action.reason)}
              >
                {action.label}
              </Button>
            );
          }
          return (
            <Button
              key={action.kind}
              variant="secondary"
              busy={enhancing}
              disabled={enhancing || !action.enabled}
              title={action.reason ?? undefined}
              onClick={async () => {
                if (!action.enabled) {
                  setRestorationNote(action.reason);
                  return;
                }
                setEnhancing(true);
                setEnhancementMessage(null);
                setRestorationNote(null);
                try {
                  setEnhancement(await enhanceMedia(asset.id));
                } catch (error) {
                  setEnhancementMessage(
                    error instanceof ApiError && error.status === 409
                      ? "Bu dosyada güvenle düzeltilebilen ölçülmüş bir sorun yok."
                      : "İyileştirme oluşturulamadı; orijinal dosya değişmedi.",
                  );
                } finally {
                  setEnhancing(false);
                }
              }}
            >
              {action.label}
            </Button>
          );
        })}
      </div>
      {restorationNote && (
        <p className="problem-media__enhancement-message" role="status">
          {restorationNote}
        </p>
      )}
      {expanded && !previewFailed && (
        <figure className="problem-media__expanded">
          <img
            src={apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`, { size: "detail" })}
            alt={`${asset.original_filename} büyük önizlemesi`}
            decoding="async"
            onError={() => setPreviewFailed(true)}
          />
          <figcaption className="faint">
            {asset.media_type === "video"
              ? "Videodan alınmış tek kare. Oynatma yapılmaz."
              : "Küçültülmüş önizleme; yüklediğiniz dosyanın kendisi değildir."}
          </figcaption>
        </figure>
      )}
      {enhancementMessage && <p className="problem-media__enhancement-message" role="status">{enhancementMessage}</p>}
      {enhancement && <EnhancementComparison asset={asset} item={enhancement} />}
    </li>
  );
}

const TRANSFORMATION_LABELS: Record<string,string> = {
  exif_orientation_normalization:"EXIF yönü normalize edildi",
  conservative_tonal_normalization:"Pozlama/kontrast konservatif olarak düzenlendi",
  mild_sharpening:"Kontrollü keskinleştirme uygulandı",
  deterministic_upscale:"Piksel boyutları artırıldı; gerçek fiziksel detay kazanımı iddia edilmez",
  // Without a row here the raw code lands on the screen, which is the
  // §21 leak this release already fixed once on the coverage panel.
  model_super_resolution:"Yapay zekâ ile büyütüldü; detay geri getirilmedi, üretildi",
};

function EnhancementComparison({asset,item}:{asset:MediaAsset;item:MediaEnhancement}) {
  return <section className="enhancement-comparison" aria-label={`${asset.original_filename} önce ve sonra karşılaştırması`}>
    <div className="enhancement-comparison__images">
      <figure><img src={apiUrl(`/media/${encodeURIComponent(asset.id)}/preview`)} alt={`${asset.original_filename}, orijinal`} /><figcaption>ORİJİNAL · {item.original_width}×{item.original_height}</figcaption></figure>
      <figure><img src={apiUrl(`/media/${encodeURIComponent(asset.id)}/enhancements/${encodeURIComponent(item.id)}/preview`)} alt={`${asset.original_filename}, iyileştirilmiş türev`} /><figcaption>İYİLEŞTİRİLMİŞ · {item.output_width}×{item.output_height}</figcaption></figure>
    </div>
    <div className="enhancement-comparison__facts">
      <StatusBadge tone={item.beneficial ? "success" : "caution"}>{item.beneficial ? "Ölçülen metrik iyileşti" : "İnceleme gerekli"}</StatusBadge>
      <span>Keskinlik {formatDecimal(item.before_metrics.sharpness)} → {formatDecimal(item.after_metrics.sharpness)}</span>
      <span>Pozlama {formatDecimal(item.before_metrics.exposure)} → {formatDecimal(item.after_metrics.exposure)}</span>
      <span>Kontrast {formatDecimal(item.before_metrics.contrast)} → {formatDecimal(item.after_metrics.contrast)}</span>
      <ul>{item.transformations.map((code)=><li key={code}>{TRANSFORMATION_LABELS[code] ?? code}</li>)}</ul>
      <p className="faint">Orijinal korunur. Bu türev V1'de rekonstrüksiyon girdisi değildir.</p>
    </div>
  </section>;
}

export function ProblemMediaDialog({
  rows,
  sessionLabel,
  projectId,
  initialCode,
  onClose,
  onChanged,
}: Props) {
  const navigate = useNavigate();
  const dialog = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState<string | null>(initialCode);
  /**
   * What this installation can run, read once for the whole dialog.
   *
   * Once rather than per row: it describes the server, not the file, and forty
   * rows asking the same question forty times is forty requests for one
   * answer. A failure leaves it null, which keeps every learned action
   * disabled - the safe direction.
   */
  const [capabilities, setCapabilities] = useState<ModelCapabilities | null>(null);
  useEffect(() => {
    let cancelled = false;
    const read = async () => {
      try {
        const result = await getModelCapabilities();
        if (!cancelled) setCapabilities(result);
      } catch {
        // Left null. Nothing is offered that cannot be performed.
      }
    };
    void read();
    return () => {
      cancelled = true;
    };
  }, []);

  useDialogFocus(dialog, { onEscape: onClose });

  /**
   * The filter chips, counted from the rows on screen.
   *
   * Built from the data rather than from a list of known codes: a chip that
   * selects nothing is a control that does nothing, and the set of codes a
   * session produced is the only set worth offering.
   */
  const codes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const { assessment } of rows) {
      for (const issue of assessment.issues ?? []) {
        counts.set(issue.code, (counts.get(issue.code) ?? 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const visible = useMemo(
    () =>
      code === null
        ? rows
        : rows.filter(({ assessment }) =>
            (assessment.issues ?? []).some((issue) => issue.code === code),
          ),
    [rows, code],
  );

  /**
   * The files on screen the deterministic pipeline can actually help.
   *
   * From `visible` rather than from every row, so the button acts on what the
   * reader is looking at: they filtered to one finding and the batch is that
   * finding's files.
   */
  const batch = useMemo(
    () =>
      eligibleFor(
        "enhance",
        visible.map(({ asset, assessment }) => ({
          asset,
          analysis: { state: "assessed" as const, assessment },
        })),
      ),
    [visible],
  );
  const [batching, setBatching] = useState(false);
  const [batchReport, setBatchReport] = useState<string | null>(null);

  const runBatch = async () => {
    setBatching(true);
    setBatchReport(null);
    const result = await runBulk(batch, (id) => enhanceMedia(id));
    setBatching(false);
    setBatchReport(
      result.failed.length === 0
        ? `${result.succeeded.length} dosya iyileştirildi. Orijinaller değişmedi.`
        : `${result.succeeded.length} / ${batch.length} dosya iyileştirildi. ` +
          `${result.failed.length} dosya değişmedi.`,
    );
    // Only when something changed. A batch that failed entirely leaves the
    // list exactly as it was and re-reading would look like an action.
    if (result.succeeded.length > 0) onChanged();
  };

  // A filter that outlives its data: deleting the last file with a code leaves
  // the chip selected and the list empty, which reads as "nothing here" rather
  // than "you removed them all".
  useEffect(() => {
    if (code !== null && !codes.some(([value]) => value === code)) setCode(null);
  }, [code, codes]);

  return (
    <div className="pairing-backdrop" role="presentation" onClick={onClose}>
      <div
        className="pairing-dialog problem-media"
        role="dialog"
        aria-modal="true"
        aria-labelledby="problem-media-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="problem-media-title">Kalite sorunu bulunan dosyalar</h2>
        <p className="pairing-muted">
          Yalnızca ölçülen bulgular listelenir. Bir dosyanın hangi değerde
          yetersiz bulunduğu, ölçüm kaydı varsa yanında gösterilir.
        </p>

        {codes.length > 1 && (
          <div className="problem-media__filters" role="group" aria-label="Bulguya göre süz">
            <button
              type="button"
              className={`chip chip--filter${code === null ? " chip--on" : ""}`}
              aria-pressed={code === null}
              onClick={() => setCode(null)}
            >
              Tümü ({rows.length})
            </button>
            {codes.map(([value, count]) => (
              <button
                key={value}
                type="button"
                className={`chip chip--filter${code === value ? " chip--on" : ""}`}
                aria-pressed={code === value}
                onClick={() => setCode(value)}
              >
                {qualityIssueText(value)} ({count})
              </button>
            ))}
          </div>
        )}

        {batchReport && (
          <p className="pairing-muted" role="status">
            {batchReport}
          </p>
        )}

        {visible.length === 0 ? (
          <p className="pairing-muted">Bu süzgece uyan dosya kalmadı.</p>
        ) : (
          <ul className="problem-media__list" aria-label="Sorunlu dosyalar">
            {visible.map((row) => (
              <ProblemRow
                key={row.asset.id}
                row={row}
                sessionLabel={sessionLabel}
                capabilities={capabilities}
                onChanged={onChanged}
              />
            ))}
          </ul>
        )}

        <div className="problem-media__footer">
          {/*
            The bridge from a finding to a fix: the reader arrived here by
            pressing "12 dosyada: Netlik yetersiz", and this acts on exactly
            the files behind that number - the eligible ones, decided from the
            same measurement, so the batch cannot include a file the single
            path would refuse.
          */}
          {batch.length > 0 && (
            <Button
              busy={batching}
              disabled={batching}
              onClick={() => void runBatch()}
            >
              {`Bu listedeki ${batch.length} dosyayı iyileştir`}
            </Button>
          )}
          <Button onClick={() => navigate(paths.newCorrectionSession(projectId))}>
            Düzeltme çekimi başlat
          </Button>
          <Button variant="primary" onClick={onClose}>
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
}
